import React, {useEffect, useState} from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import HeaderSection from './HeaderSection';
import FooterSection from './FooterSection';
import MainArea from './MainArea';
import ProgrammedCalls from "./ProgrammedCalls";
import { InstallationsContext, GenericContext, MqttContext } from './App';
import {ProgrammedCallCallInterface} from "./ProgrammedCallCallInterface";
import JournalInterface from "./JournalInterface";
import PermanentAlertsBar from "./PermanentAlertsBar";
import dateAdd from "date-fns/add";
import {dateToTimestamp, timestampToDate} from "../utilities";
import {JOURNAL_ENTRY_TYPE_PROG_CALL} from "../api/wimhome-api";
import {confirmAlert} from "react-confirm-alert";
import {SITE_LINKS} from "../Contants";

export default withRouter(function ProgrammedCallsPage(props) {
  const {
    currRoute,
    history,
  } = props;
  const {t, /*i18n*/} = useTranslation();

  const {
    selectedInst,
    installations,
    createInstJournalEntry,
    handleSelectInstallation,
    loadInstallationsProgrammedCalls
  } = React.useContext(InstallationsContext);
  const { errors, userType } = React.useContext(GenericContext);
  const { mqttStatus } = React.useContext(MqttContext);

  let mainContentRef = React.createRef();

  const [programmedCall, setProgrammedCall] = useState(null)
  const [journalModalType, setJournalModalType] = useState('closed');

  const [programmedCalls, setProgrammedCalls] = useState({"loaded": false, "calls": []});

  function saveCall(installationSerial, lastCall, description, result, willRetry) {
    // console.log(lastCall)

    // let nextCallTime = dateToTimestamp(dateAdd(timestampToDate(lastCall.caseData.nextCall), {days: lastCall.caseData.daysBetweenCalls}))
    let nextCallTime = dateAdd(timestampToDate(lastCall.caseData.nextCall), {days: lastCall.caseData.daysBetweenCalls})
    const now = new Date();
    while(nextCallTime < now) {
      nextCallTime = dateAdd(nextCallTime, {days: lastCall.caseData.daysBetweenCalls})
    }

    if(willRetry) {
      nextCallTime = lastCall.caseData.nextCall; // does not change the next try date to not throw off the periodicity
    } else {
      nextCallTime = dateToTimestamp(nextCallTime)
    }
    // console.log(nextCallTime)

    let newCall = {...lastCall,
      title: lastCall.title,
      description: description,
      type: JOURNAL_ENTRY_TYPE_PROG_CALL,
      data: {
        daysBetweenCalls: lastCall.caseData.daysBetweenCalls,
        nextCall: nextCallTime,
        result: result,
        willRetry: willRetry
      }
    };
    createInstJournalEntry(installationSerial, newCall)
        .then((savedJournal) => {
          if(savedJournal != null) {
            setProgrammedCall({"installationSerial": null, "installationTitle": null, "lastCall": null})
            reloadProgrammedCalls();
          } else {
            confirmAlert({
              title: `${t('programmedCalls.errorTitle')}`,
              message: `${t('programmedCalls.cannotSaveCall')}`,
              buttons: [
                {
                  label: t('OK'),
                }
              ],
              closeOnEscape: false,
              closeOnClickOutside: false,
              overlayClassName: "overlay-confirm-alert-over-all"
            })
          }
        })
        .catch((err) => {
          confirmAlert({
            title: `${t('programmedCalls.errorTitle')}`,
            message: `${t('programmedCalls.cannotSaveCall') + " error: " + err.message}`,
            buttons: [
              {
                label: t('OK'),
              }
            ],
            closeOnEscape: false,
            closeOnClickOutside: false,
            overlayClassName: "overlay-confirm-alert-over-all"
          })
        })
  }

  function abortCall() {
    setProgrammedCall({"installationSerial": null, "installationTitle": null, "lastCall": null})
  }

  async function loadProgrammedCalls(force = false) {
    if(!(installations instanceof Object))
      return null;

    if(programmedCalls?.loaded === true && force === false) {
      return programmedCalls.calls;
    }
    // console.log(installations);
    let newEntries = []

    let phoneCalls = await loadInstallationsProgrammedCalls(false);
    if(phoneCalls == null) {
      return null;
    }
    for(const key in phoneCalls) {
      const installation = installations[key];
      phoneCalls[key][0].installationSerial = installation.id;
      phoneCalls[key][0].installationTitle = installation.title
      newEntries.push(phoneCalls[key][0]);
    }

    return newEntries;
  }

  function reloadProgrammedCalls() {
    // console.log(installations)
    loadProgrammedCalls(true)
        .then((newEntries) => {
          // console.log(newEntries);
          if(newEntries != null) {
            setProgrammedCalls({"loaded": true, "calls": newEntries})
          }
        })
  }

  useEffect(() => {
    if(userType !== "remote_assistance") {
      setProgrammedCalls({"loaded": false, "calls": []})
      return;
    }

    // console.log(installations)
    if(installations == null) {
      setProgrammedCalls({"loaded": false, "calls": []})
      return
    }

    loadProgrammedCalls(false)
        .then((newEntries) => {
          // console.log(newEntries);
          if(newEntries != null) {
            setProgrammedCalls({"loaded": true, "calls": newEntries})
          }
        })
  }, [installations]);

  useEffect(() => {
    if(userType == null) {
      return;
    }
    if(userType !== "remote_assistance") {
      alert(`${t('forbiddenPageAccess')}`)
      history.push(SITE_LINKS.INSTALLATIONS);
      return;
    }
    if(journalModalType === 'closing' && selectedInst != null) {
      setJournalModalType('closed')
      handleSelectInstallation(null);
    }
  }, [journalModalType]);

  if(userType == null) {
    return <div>{`${t('loadingMessage')}`}</div>;
  }
  if(userType !== "remote_assistance") {
    history.push(SITE_LINKS.INSTALLATIONS);
    return null;
  }

  return (
    <>
      <ProgrammedCallCallInterface
          abort={abortCall}
          installationSerial={programmedCall?.installationSerial}
          installationTitle={programmedCall?.installationTitle}
          lastCall={programmedCall?.lastCall}
          saveCall={saveCall}
      />
      <HeaderSection
          currRoute={currRoute}
          errors={errors}
          mainContentRef={mainContentRef}
          mqttStatus={mqttStatus}
          selectedInst={selectedInst}
      />
      <section className="main-content" ref={mainContentRef}>
        <PermanentAlertsBar />
        <MainArea
            headerAddOns={
              <span
                  className='options-menu-icon'
              >
              </span>
            }
            title={`${t('programmedCalls.title')}`}
        >
          {userType === "remote_assistance" &&
              <>
                {(journalModalType !== 'closed') && (
                    <JournalInterface
                        onlyProgrammedCalls={true}
                        setType={setJournalModalType}
                        showInstallationTitle={true}
                        standalone={true} type={journalModalType}
                    />
                )}
                <ProgrammedCalls
                    programmedCalls={programmedCalls}
                    setJournalModalType={setJournalModalType}
                    setProgrammedCall={setProgrammedCall}
                    title={null}
                />
              </>
          }
        </MainArea>
      </section>
      <FooterSection/>
    </>
)
});